import { lazy } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

const FeeCommissionObjectRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/instructions/fee-payouts/${id ?? ''}`} replace />;
};

const LegacyCardKycRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/cms/issuer-card-kyc${location.search}`} replace />;
};

const e2eRoutes = import.meta.env.DEV
  ? [{
      path: '/__e2e__/split-view/:id',
      component: lazy(() => import('../pages/e2e/split-view'))
    }]
  : [];

// Lazy load all pages
export const routes = [
  // Public routes (no layout)
  {
    path: '/login',
    component: lazy(() => import('../pages/login'))
  },

  // Protected routes (with layout)
  {
    path: '/dashboard',
    component: lazy(() => import('../pages/welcome'))
  },
  ...e2eRoutes,
  {
    path: '/users',
    component: lazy(() => import('../pages/users'))
  },
  {
    path: '/users/groups',
    component: () => <Navigate to="/user-groups" replace />
  },
  {
    path: '/users/client-migrations',
    component: lazy(() => import('../pages/client-migrations'))
  },
  {
    path: '/users/client-migrations/:campaignId',
    component: lazy(() => import('../pages/client-migrations'))
  },
  {
    path: '/users/:id',
    component: lazy(() => import('../pages/users/details'))
  },
  {
    path: '/welcome-email-templates',
    component: lazy(() => import('../pages/welcome-email-templates'))
  },
  {
    path: '/pending-instructions/payments',
    component: lazy(() => import('../pages/pending-instructions/payment/index'))
  },
  {
    path: '/pending-instructions/instructions',
    component: lazy(() => import('../pages/pending-instructions/instruction/index'))
  },
  {
    path: '/pending-instructions/cra-form',
    component: lazy(() => import('../pages/cra-form/index'))
  },
  {
    path: '/solution-application',
    component: lazy(() => import('../pages/solution-application/index'))
  },
  {
    path: '/client-account/wallet-accounts',
    component: lazy(() => import('../pages/wallet-account/index'))
  },
  {
    path: '/client-account/client-deposit-accounts',
    component: lazy(() => import('../pages/client-deposit-accounts/index'))
  },
  {
    path: '/client-account/od-account',
    component: lazy(() => import('../pages/od-account/index'))
  },
  {
    path: '/client-account/bank-payout-whitelist',
    component: lazy(() => import('../pages/bank-payout-whitelist/index'))
  },
  {
    path: '/client-documents',
    component: lazy(() => import('../pages/client-document/index'))
  },
  {
    path: '/document-templates',
    component: lazy(() => import('../pages/pdf-template-management/index'))
  },
  {
    path: '/name-check',
    component: lazy(() => import('../pages/name-check/index'))
  },
  {
    path: '/name-check/settings',
    component: lazy(() => import('../pages/name-check/settings/index'))
  },
  {
    path: '/name-check/:id',
    component: lazy(() => import('../pages/name-check-detail/index'))
  },
  {
    path: '/instructions/all',
    component: lazy(() => import('../pages/all-instruction-list/index'))
  },
  {
    path: '/instructions/scheduled',
    component: lazy(() => import('../pages/scheduled-instruction-list/index'))
  },
  {
    path: '/instructions/general-instruction',
    component: lazy(() => import('../pages/general-instruction/index'))
  },
  {
    path: '/instructions/general-instruction/:id',
    component: lazy(() => import('../pages/general-instruction/detail'))
  },
  {
    path: '/instructions/fiat-withdraws',
    component: lazy(() => import('../pages/fiat-withdraw/index'))
  },
  {
    path: '/instructions/fiat-withdraws/:id',
    component: lazy(() => import('../pages/fiat-withdraw/detail'))
  },
  {
    path: '/instructions/fiat-deposits',
    component: lazy(() => import('../pages/fiat-deposit/index'))
  },
  {
    path: '/instructions/fiat-deposits/new',
    component: lazy(() => import('../pages/fiat-deposit/new'))
  },
  {
    path: '/instructions/fiat-deposits/:id',
    component: lazy(() => import('../pages/fiat-deposit/detail'))
  },
  {
    path: '/instructions/cash-in-out',
    component: lazy(() => import('../pages/cash-in-out/index'))
  },
  {
    path: '/instructions/transfers',
    component: lazy(() => import('../pages/transfers/index'))
  },
  {
    path: '/instructions/transfers/:id',
    component: lazy(() => import('../pages/transfers/detail'))
  },
  {
    path: '/instructions/bill-payments',
    component: lazy(() => import('../pages/bill-payments/index'))
  },
  {
    path: '/instructions/legacy-bill-payments',
    component: lazy(() => import('../pages/legacy-bill-payments/index'))
  },
  {
    path: '/instructions/legacy-bill-payments/new',
    component: lazy(() => import('../pages/legacy-bill-payments/new'))
  },
  {
    path: '/instructions/legacy-bill-payments/:id',
    component: lazy(() => import('../pages/legacy-bill-payments/detail'))
  },
  {
    path: '/instructions/exchanges',
    component: lazy(() => import('../pages/exchanges/index'))
  },
  {
    path: '/instructions/exchanges/quick',
    component: lazy(() => import('../pages/exchanges/quick/index'))
  },
  {
    path: '/instructions/exchanges/custom-rate',
    component: lazy(() => import('../pages/exchanges/custom-rate/index'))
  },
  {
    path: '/instructions/exchanges/:id',
    component: lazy(() => import('../pages/exchanges/detail'))
  },
  {
    path: '/instructions/fee-payouts/:id',
    component: lazy(() => import('../pages/fee-payout/detail'))
  },
  {
    path: '/objects/fee_commission/:id',
    component: FeeCommissionObjectRedirect
  },
  {
    path: '/instructions/unionpay-remittance',
    component: lazy(() => import('../pages/remittance/unionpay'))
  },
  {
    path: '/instructions/alipay-remittance',
    component: lazy(() => import('../pages/remittance/alipay'))
  },
  {
    path: '/digital-custody/deposits/pending',
    component: lazy(() => import('../pages/digital-deposit/digitalDepositPending'))
  },
  {
    path: '/digital-custody/deposits',
    component: lazy(() => import('../pages/digital-deposit/index'))
  },
  {
    path: '/digital-custody/deposits/:id',
    component: lazy(() => import('../pages/digital-deposit/detail'))
  },
  {
    path: '/digital-custody/withdraws',
    component: lazy(() => import('../pages/digital-withdraw/index'))
  },
  {
    path: '/digital-custody/withdraws/:id',
    component: lazy(() => import('../pages/digital-withdraw/detail'))
  },
  {
    path: '/digital-custody/address-screening',
    component: lazy(() => import('../pages/address-screening/index'))
  },
  {
    path: '/digital-custody/screening-history',
    component: lazy(() => import('../pages/kyt-screening/index'))
  },
  {
    path: '/digital-custody/screening-history/:id',
    component: lazy(() => import('../pages/kyt-screening/detail'))
  },
  {
    path: '/digital-custody/relationship-graph',
    component: lazy(() => import('../pages/kyt-screening/relationship-graph'))
  },
  {
    path: '/digital-custody/batch-rescreen',
    component: lazy(() => import('../pages/batch-rescreen/index'))
  },
  // Compliance - static paths must come before dynamic :id paths
  {
    path: '/compliance/cases/rules',
    component: lazy(() => import('../pages/cases/rules'))
  },
  {
    path: '/compliance/cases',
    component: lazy(() => import('../pages/cases/index'))
  },
  {
    path: '/compliance/cases/:id',
    component: lazy(() => import('../pages/cases/detail'))
  },
  {
    path: '/compliance/kyt-screening',
    component: lazy(() => import('../pages/kyt-screening/index'))
  },
  {
    path: '/compliance/kyt-screening/:id',
    component: lazy(() => import('../pages/kyt-screening/detail'))
  },
  {
    path: '/invoices',
    component: lazy(() => import('../pages/invoices/index'))
  },
  {
    path: '/invoices/:id',
    component: lazy(() => import('../pages/invoices/detail'))
  },
  // Card Programs routes
  {
    path: '/card-programs',
    component: lazy(() => import('../pages/card-programs/index'))
  },
  {
    path: '/card-programs/:id',
    component: lazy(() => import('../pages/card-programs/details'))
  },
  // CMS routes
  {
    path: '/cms/cards',
    component: lazy(() => import('../pages/cms/cards/index'))
  },
  {
    path: '/cms/cards/:id/:userId',
    component: lazy(() => import('../pages/cms/cards/details'))
  },
  {
    path: '/cms/card-kyc',
    component: LegacyCardKycRedirect
  },
  {
    path: '/cms/issuer-card-kyc',
    component: lazy(() => import('../pages/cms/card-kyc/index'))
  },
  {
    path: '/cms/card-kyc-status',
    component: lazy(() => import('../pages/cms/card-kyc-status/index'))
  },
  {
    path: '/cms/card-factory',
    component: lazy(() => import('../pages/cms/card-factory/index'))
  },
  {
    path: '/cms/card-account',
    component: lazy(() => import('../pages/cms/card-account/index'))
  },
  {
    path: '/cms/card-accounts/:id/:userId',
    component: lazy(() => import('../pages/cms/card-accounts/details'))
  },
  {
    path: '/cms/card-cupd-balances',
    component: lazy(() => import('../pages/cms/card-cupd-balances/index'))
  },
  {
    path: '/cms/card-balances',
    component: lazy(() => import('../pages/cms/card-balances/index'))
  },
  {
    path: '/cms/card-repayment',
    component: lazy(() => import('../pages/cms/card-repayment/index'))
  },
  {
    path: '/cms/card-application',
    component: lazy(() => import('../pages/cms/card-application/index'))
  },
  {
    path: '/cms/card-renewal',
    component: lazy(() => import('../pages/cms/card-renewal/index'))
  },
  {
    path: '/cms/card-pending-embossed',
    component: lazy(() => import('../pages/cms/card-pending-embossed/index'))
  },
  {
    path: '/cms/asset-value-config',
    component: lazy(() => import('../pages/cms/asset-value-config/index'))
  },
  {
    path: '/cms/card-products-v2',
    component: lazy(() => import('../pages/cms/card-products-v2/index'))
  },
  {
    path: '/cms/card-products-v2/:id',
    component: lazy(() => import('../pages/cms/card-products-v2/detail'))
  },
  {
    path: '/cms/transactions',
    component: lazy(() => import('../pages/cms/transaction/index'))
  },
  {
    path: '/cms/transactions/:id',
    component: lazy(() => import('../pages/cms/transaction/details'))
  },
  {
    path: '/cms/card-disputes',
    component: lazy(() => import('../pages/card-disputes/index'))
  },
  {
    path: '/cms/force-posting',
    component: lazy(() => import('../pages/cms/force-posting/index'))
  },
  {
    path: '/cms/force-posting/:id',
    component: lazy(() => import('../pages/cms/force-posting/index'))
  },
  {
    path: '/cms/card-account-interest',
    component: lazy(() => import('../pages/cms/card-account-interest/index'))
  },
  {
    path: '/cms/card-name-check',
    component: lazy(() => import('../pages/cms/card-name-check/index'))
  },
  {
    path: '/cms/card-name-check/:id',
    component: lazy(() => import('../pages/cms/card-name-check-detail/index'))
  },
  {
    path: '/cms/notification-log',
    component: lazy(() => import('../pages/cms/card-code-delivery/index'))
  },
  {
    path: '/cms/card-oob-delivery',
    component: lazy(() => import('../pages/cms/card-oob-delivery/index'))
  },
  {
    path: '/cms/tribe-tai-webhooks',
    component: lazy(() => import('../pages/cms/tribe-tai-webhooks/index'))
  },
  // Securities
  {
    path: '/securities/brokers',
    component: lazy(() => import('../pages/securities/index'))
  },
  {
    path: '/securities/accounts',
    component: lazy(() => import('../pages/securities/accounts/index'))
  },
  // Earn
  {
    path: '/earn/offers',
    component: lazy(() => import('../pages/earn/offer/index'))
  },
  {
    path: '/earn/subscriptions',
    component: lazy(() => import('../pages/earn/subscription/index'))
  },
  {
    path: '/earn/subscriptions/:id',
    component: lazy(() => import('../pages/earn/subscription/detail'))
  },
  {
    path: '/earn/position',
    component: lazy(() => import('../pages/earn/position/index'))
  },
  {
    path: '/earn/redemptions/:id',
    component: lazy(() => import('../pages/earn/redemption/detail'))
  },
  {
    path: '/earn/providers',
    component: lazy(() => import('../pages/earn/provider/index'))
  },
  // Alternatives
  {
    path: '/alternatives',
    component: lazy(() => import('../pages/alternatives/index'))
  },
  {
    path: '/alternatives/:id/:userId',
    component: lazy(() => import('../pages/alternatives/details'))
  },
  // Insurance
  {
    path: '/insurance',
    component: lazy(() => import('../pages/insurance/index'))
  },
  {
    path: '/insurance/policies',
    component: lazy(() => import('../pages/insurance/index'))
  },
  {
    path: '/insurance/companies',
    component: lazy(() => import('../pages/insurance/company/index'))
  },
  {
    path: '/insurance/:id/:userId',
    component: lazy(() => import('../pages/insurance/details'))
  },
  // NFT
  {
    path: '/nfts',
    component: lazy(() => import('../pages/nft/index'))
  },
  // Reporting
  {
    path: '/reporting/standard',
    component: lazy(() => import('../pages/ReportingV2/Standard/index'))
  },
  {
    path: '/reporting/accounting',
    component: lazy(() => import('../pages/ReportingV2/Accounting/index'))
  },
  {
    path: '/reporting/cobrander-pnl',
    component: lazy(() => import('../pages/CobranderPnl/index'))
  },
  {
    path: '/reporting/journal',
    component: lazy(() => import('../pages/ReportingV2/Journal/index'))
  },
  {
    path: '/reporting/history',
    component: lazy(() => import('../pages/ReportingV2/History/index'))
  },
  {
    path: '/reporting/sensitive-card',
    component: lazy(() => import('../pages/ReportingV2/SensitiveCardReport/index'))
  },
  {
    path: '/reporting/configurations',
    component: lazy(() => import('../pages/ReportingConfiguration/index'))
  },
  // Accounting
  {
    path: '/accounting/world-pay',
    component: lazy(() => import('../pages/world-pay/index'))
  },
  {
    path: '/accounting/journal-entries',
    component: lazy(() => import('../pages/journal-entries/index'))
  },
  {
    path: '/accounting/book-entries',
    component: lazy(() => import('../pages/book-entries/index'))
  },
  {
    path: '/accounting/ledger-account',
    component: lazy(() => import('../pages/ledger-account/index'))
  },
  {
    path: '/accounting/balance-sheet',
    component: lazy(() => import('../pages/balance-sheet/index'))
  },
  {
    path: '/accounting/bank-available-balance',
    component: lazy(() => import('../pages/bank-available-balance/index'))
  },
  {
    path: '/accounting/tags',
    component: lazy(() => import('../pages/accounting-tag-management/index'))
  },
  {
    path: '/accounting/commission-details',
    component: lazy(() => import('../pages/commission-detail/index'))
  },
  {
    path: '/accounting/sales-revenue-summary',
    component: lazy(() => import('../pages/sales-revenue-summary/index'))
  },
  {
    path: '/accounting/sales-revenue-statistics',
    component: lazy(() => import('../pages/sales-revenue-statistics/index'))
  },
  {
    path: '/accounting/revenue-breakdown',
    component: lazy(() => import('../pages/revenue-breakdown/index'))
  },
  {
    path: '/accounting/operational-accounts',
    component: lazy(() => import('../pages/operational-accounts/index'))
  },
  {
    path: '/accounting/utgl-pnl',
    component: lazy(() => import('../pages/utgl-pnl/index'))
  },
  {
    path: '/accounting/unigroup-client-asset-balance',
    component: lazy(() => import('../pages/unigroup-client-asset-balance/index'))
  },
  {
    path: '/accounting/unigroup-book-management',
    component: lazy(() => import('../pages/unigroup-book-management/index'))
  },
  // Cost and Fees
  {
    path: '/cost-and-fees/cost-management',
    component: lazy(() => import('../pages/cost-and-fees/cost-management/index'))
  },
  {
    path: '/cost-and-fees/fee-markup-management',
    component: lazy(() => import('../pages/cost-and-fees/fee-markup-management/index'))
  },
  {
    path: '/cost-and-fees/fee-schedule',
    component: lazy(() => import('../pages/cost-and-fees/fee-schedule/index'))
  },
  {
    path: '/cost-and-fees/fee-schedule-v2',
    component: lazy(() => import('../pages/cost-and-fees/fee-schedule-v2/index'))
  },
  {
    path: '/cost-and-fees/users-fee-schedule',
    component: lazy(() => import('../pages/cost-and-fees/users-fee-schedule/index'))
  },
  {
    path: '/cost-and-fees/cobrands-fee-schedule',
    component: lazy(() => import('../pages/cost-and-fees/cobrands-fee-schedule/index'))
  },
  {
    path: '/cost-and-fees/cobrands-fee-schedule/:cobrandId',
    component: lazy(() => import('../pages/cost-and-fees/cobrands-fee-schedule/index'))
  },
  {
    path: '/cost-and-fees/commission-management',
    component: lazy(() => import('../pages/commission-payout/index'))
  },
  {
    path: '/cost-and-fees/fee-setting-request',
    component: lazy(() => import('../pages/fee-setting-request/index'))
  },
  {
    path: '/cost-and-fees/agent-onboarding',
    component: lazy(() => import('../pages/cost-and-fees/components/agent/index'))
  },
  // Transaction Monitoring
  {
    path: '/transaction-monitoring/fraud-transactions',
    component: lazy(() => import('../pages/card-fraud-system/index'))
  },
  {
    path: '/transaction-monitoring/card-fraud-rules-configuration',
    component: lazy(() => import('../pages/card-fraud-system/configuration'))
  },
  {
    path: '/transaction-monitoring/blacklist-merchants',
    component: lazy(() => import('../pages/blacklist-merchant/index'))
  },
  {
    path: '/transaction-monitoring/whitelist-merchants',
    component: lazy(() => import('../pages/whitelist-merchant/index'))
  },
  // Rate Source
  {
    path: '/rate-source/configuration',
    component: lazy(() => import('../pages/rate-source/configuration/index'))
  },
  {
    path: '/rate-source/rate-reference',
    component: lazy(() => import('../pages/rate-source/rate-reference/index'))
  },
  {
    path: '/rate-source/rate-board',
    component: lazy(() => import('../pages/rate-source/rate-board/index'))
  },
  // Precious Metal
  {
    path: '/precious-metal/products',
    component: lazy(() => import('../pages/precious-metal/products/index'))
  },
  {
    path: '/precious-metal/instructions',
    component: lazy(() => import('../pages/precious-metal/instructions/index'))
  },
  {
    path: '/precious-metal/instructions/:id',
    component: lazy(() => import('../pages/precious-metal/instructions/details'))
  },
  {
    path: '/precious-metal/positions',
    component: lazy(() => import('../pages/precious-metal/positions/index'))
  },
  // Fund
  {
    path: '/fund/positions',
    component: lazy(() => import('../pages/fund/position/index'))
  },
  {
    path: '/fund/instructions',
    component: lazy(() => import('../pages/fund/instruction/index'))
  },
  {
    path: '/fund/instructions/:id',
    component: lazy(() => import('../pages/fund/instruction/details'))
  },
  // Purchase Order
  {
    path: '/purchase-order',
    component: lazy(() => import('../pages/purchase-order/index'))
  },
  // Tag Management
  {
    path: '/tag-management',
    component: lazy(() => import('../pages/tag-management/index'))
  },
  // Invitation Code
  {
    path: '/invitation-code',
    component: lazy(() => import('../pages/invitation-code/index'))
  },
  // RM Account Analysis
  {
    path: '/flow-analysis',
    component: lazy(() => import('../pages/flow-analysis/index'))
  },
  // Relationship Managers
  {
    path: '/relationship-managers',
    component: lazy(() => import('../pages/relationship-managers/index'))
  },
  // iFast Accounts
  {
    path: '/fund/ifast-accounts',
    component: lazy(() => import('../pages/ifast-accounts/index'))
  },
  // User Groups
  {
    path: '/user-groups',
    component: lazy(() => import('../pages/cost-and-fees/user-group-management/index'))
  },
  {
    path: '/user-groups/:id',
    component: lazy(() => import('../pages/cost-and-fees/user-group-management/details'))
  },
  // Support Center
  {
    path: '/support-center/client-requests',
    component: lazy(() => import('../pages/support-center/clientRequests'))
  },
  {
    path: '/support-center/client-requests/:id',
    component: lazy(() => import('../pages/support-center/client-detail'))
  },
  {
    path: '/support-center/all-requests',
    component: lazy(() => import('../pages/support-center/index'))
  },
  {
    path: '/support-center/my-requests',
    component: lazy(() => import('../pages/support-center/myRequest'))
  },
  {
    path: '/support-center/all-requests/:id',
    component: lazy(() => import('../pages/support-center/detail'))
  },
  /** Legacy inbox / notification links used `/support-center/detail/:id` (no list segment). */
  {
    path: '/support-center/detail/:id',
    component: lazy(() => import('../pages/support-center/detail'))
  },
  {
    path: '/support-center/my-requests/:id',
    component: lazy(() => import('../pages/support-center/detail'))
  },
  {
    path: '/support-center/team-board',
    component: lazy(() => import('../pages/support-center/teamBoard'))
  },
  {
    path: '/support-center/team-board/:id',
    component: lazy(() => import('../pages/support-center/detail'))
  },
  {
    path: '/support-center/new',
    component: lazy(() => import('../pages/support-center/new'))
  },
  {
    path: '/support-center/report-issue',
    component: lazy(() => import('../pages/support-center/report-issue'))
  },
  {
    path: '/support-center/feedback',
    component: lazy(() => import('../pages/support-center/feedback'))
  },
  {
    path: '/support-center/feature-request',
    component: lazy(() => import('../pages/support-center/feature-request'))
  },
  {
    path: '/support-center/client-issue',
    component: lazy(() => import('../pages/support-center/client-issue'))
  },
  {
    path: '/support-center/category-admin',
    component: lazy(() => import('../pages/support-center/category-admin'))
  },
  {
    path: '/support-center/tag-admin',
    component: lazy(() => import('../pages/support-center/tag-admin'))
  },
  {
    path: '/support-center/category-analytics',
    component: lazy(() => import('../pages/support-center/category-analytics'))
  },
  // Manual
  {
    path: '/manual',
    component: lazy(() => import('../pages/manual/index'))
  },
  // Getting Started Manual
  {
    path: '/manual/getting-started',
    component: lazy(() => import('../pages/manual/getting-started'))
  },
  {
    path: '/manual/getting-started/platform-overview',
    component: lazy(() => import('../pages/manual/content/getting-started/platform-overview.mdx'))
  },
  {
    path: '/manual/getting-started/authentication',
    component: lazy(() => import('../pages/manual/content/getting-started/authentication.mdx'))
  },
  {
    path: '/manual/getting-started/inbox-messaging',
    component: lazy(() => import('../pages/manual/content/getting-started/inbox-messaging.mdx'))
  },
  {
    path: '/manual/getting-started/keyboard-shortcuts',
    component: lazy(() => import('../pages/manual/content/getting-started/keyboard-shortcuts.mdx'))
  },
  {
    path: '/manual/getting-started/app-important-notices',
    component: lazy(() => import('../pages/manual/content/getting-started/app-important-notices.mdx'))
  },
  // Payments Manual
  {
    path: '/manual/payments',
    component: lazy(() => import('../pages/manual/payments'))
  },
  {
    path: '/manual/payments/pending-payments',
    component: lazy(() => import('../pages/manual/content/payments/pending-payments.mdx'))
  },
  {
    path: '/manual/payments/pending-instructions',
    component: lazy(() => import('../pages/manual/content/payments/pending-instructions.mdx'))
  },
  {
    path: '/manual/payments/fiat-deposits',
    component: lazy(() => import('../pages/manual/content/payments/fiat-deposits.mdx'))
  },
  {
    path: '/manual/payments/fiat-withdrawals',
    component: lazy(() => import('../pages/manual/content/payments/fiat-withdrawals.mdx'))
  },
  {
    path: '/manual/payments/transfers',
    component: lazy(() => import('../pages/manual/content/payments/transfers.mdx'))
  },
  {
    path: '/manual/payments/exchanges',
    component: lazy(() => import('../pages/manual/content/payments/exchanges.mdx'))
  },
  {
    path: '/manual/payments/general-instructions',
    component: lazy(() => import('../pages/manual/content/payments/general-instructions.mdx'))
  },
  {
    path: '/manual/payments/all-instructions',
    component: lazy(() => import('../pages/manual/content/payments/all-instructions.mdx'))
  },
  // Reporting Manual
  {
    path: '/manual/reporting',
    component: lazy(() => import('../pages/manual/reporting'))
  },
  {
    path: '/manual/reporting/cobrander-pnl',
    component: lazy(() => import('../pages/manual/content/reporting/cobrander-pnl.mdx'))
  },
  {
    path: '/manual/reporting/utgl-pnl',
    component: lazy(() => import('../pages/manual/content/reporting/utgl-pnl.mdx'))
  },
  // Client Onboarding Manual
  {
    path: '/manual/client-onboarding',
    component: lazy(() => import('../pages/manual/client-onboarding'))
  },
  {
    path: '/manual/client-onboarding/solution-applications',
    component: lazy(() => import('../pages/manual/content/client-onboarding/solution-applications.mdx'))
  },
  // Cards & Credit Manual
  {
    path: '/manual/cards-credit',
    component: lazy(() => import('../pages/manual/cards-credit'))
  },
  {
    path: '/manual/cards-credit/card-applications',
    component: lazy(() => import('../pages/manual/content/cards-credit/card-applications.mdx'))
  },
  {
    path: '/manual/cards-credit/card-transactions',
    component: lazy(() => import('../pages/manual/content/cards-credit/card-transactions.mdx'))
  },
  {
    path: '/manual/cards-credit/card-cancellation',
    component: lazy(() => import('../pages/manual/content/cards-credit/card-cancellation.mdx'))
  },
  {
    path: '/manual/cards-credit/card-renewals',
    component: lazy(() => import('../pages/manual/content/cards-credit/card-renewals.mdx'))
  },
  {
    path: '/manual/cards-credit/cupd-mismatch-reconciliation',
    component: lazy(() => import('../pages/manual/content/cards-credit/cupd-mismatch-reconciliation.mdx'))
  },
  {
    path: '/manual/cards-credit/program-facility',
    component: lazy(() => import('../pages/manual/content/cards-credit/program-facility.mdx'))
  },
  {
    path: '/manual/cases',
    component: lazy(() => import('../pages/manual/case-management'))
  },
  {
    path: '/manual/case-management',
    component: lazy(() => import('../pages/manual/case-management'))
  },
  {
    path: '/manual/case-management/overview',
    component: lazy(() => import('../pages/manual/content/case-management/overview.mdx'))
  },
  {
    path: '/manual/case-management/investigation',
    component: lazy(() => import('../pages/manual/content/case-management/investigation.mdx'))
  },
  {
    path: '/manual/case-management/edd-collection',
    component: lazy(() => import('../pages/manual/content/case-management/edd-collection.mdx'))
  },
  {
    path: '/manual/case-management/rfi-management',
    component: lazy(() => import('../pages/manual/content/case-management/rfi-management.mdx'))
  },
  {
    path: '/manual/case-management/evidence-management',
    component: lazy(() => import('../pages/manual/content/case-management/evidence-management.mdx'))
  },
  {
    path: '/manual/case-management/resolution-approval',
    component: lazy(() => import('../pages/manual/content/case-management/resolution-approval.mdx'))
  },
  {
    path: '/manual/case-management/rules-automation',
    component: lazy(() => import('../pages/manual/content/case-management/rules-automation.mdx'))
  },
  {
    path: '/manual/digital-custody',
    component: lazy(() => import('../pages/manual/digital-custody'))
  },
  {
    path: '/manual/digital-custody/deposits',
    component: lazy(() => import('../pages/manual/content/digital-custody/deposits.mdx'))
  },
  {
    path: '/manual/digital-custody/withdrawals',
    component: lazy(() => import('../pages/manual/content/digital-custody/withdrawals.mdx'))
  },
  {
    path: '/manual/digital-custody/screening-guide',
    component: lazy(() => import('../pages/manual/content/digital-custody/screening-guide.mdx'))
  },
  {
    path: '/manual/digital-custody/navigating-blockchain',
    component: lazy(() => import('../pages/manual/content/blockchain-operations/navigating-blockchain.mdx'))
  },
  // User Management Manual
  {
    path: '/manual/users',
    component: lazy(() => import('../pages/manual/user-management'))
  },
  {
    path: '/manual/user-management',
    component: lazy(() => import('../pages/manual/user-management'))
  },
  {
    path: '/manual/user-management/overview',
    component: lazy(() => import('../pages/manual/content/user-management/overview.mdx'))
  },
  {
    path: '/manual/user-management/kyc-verification',
    component: lazy(() => import('../pages/manual/content/user-management/kyc-verification.mdx'))
  },
  {
    path: '/manual/user-management/risk-assessment',
    component: lazy(() => import('../pages/manual/content/user-management/risk-assessment.mdx'))
  },
  {
    path: '/manual/user-management/name-check',
    component: lazy(() => import('../pages/manual/content/user-management/name-check.mdx'))
  },
  {
    path: '/manual/user-management/account-management',
    component: lazy(() => import('../pages/manual/content/user-management/account-management.mdx'))
  },
  {
    path: '/manual/user-management/client-migrations',
    component: lazy(() => import('../pages/manual/content/user-management/client-migrations.mdx'))
  },
  {
    path: '/manual/user-management/cra-forms',
    component: lazy(() => import('../pages/manual/content/user-management/cra-forms.mdx'))
  },
  {
    path: '/manual/user-management/welcome-email-templates',
    component: lazy(() => import('../pages/manual/content/user-management/welcome-email-templates.mdx'))
  },
  // Digital Staff Manual
  {
    path: '/manual/digital-staff',
    component: lazy(() => import('../pages/manual/digital-staff'))
  },
  {
    path: '/manual/digital-staff/overview',
    component: lazy(() => import('../pages/manual/content/digital-staff/overview.mdx'))
  },
  // Digital Asset Treasury Manual
  {
    path: '/manual/digital-asset-treasury',
    component: lazy(() => import('../pages/manual/digital-asset-treasury'))
  },
  {
    path: '/manual/digital-asset-treasury/tron-energy-rental',
    component: lazy(() => import('../pages/manual/content/digital-asset-treasury/tron-energy-rental.mdx'))
  },
  // Card Offers management
  {
    path: '/app-card-offers/new',
    component: lazy(() => import('../pages/app-card-offers/FormPage'))
  },
  {
    path: '/app-card-offers/:id',
    component: lazy(() => import('../pages/app-card-offers/FormPage'))
  },
  {
    path: '/app-card-offers',
    component: lazy(() => import('../pages/app-card-offers/ListPage'))
  },
  // App Important Notices management
  {
    path: '/app-important-notices/new',
    component: lazy(() => import('../pages/app-important-notices/FormPage'))
  },
  {
    path: '/app-important-notices/:id',
    component: lazy(() => import('../pages/app-important-notices/FormPage'))
  },
  {
    path: '/app-important-notices',
    component: lazy(() => import('../pages/app-important-notices/ListPage'))
  },
  { path: '/manual/system/app-disclosures', component: lazy(() => import('../pages/manual/content/system/app-disclosures.mdx')) },
  { path: '/manual/system/cs-telegram-bot', component: lazy(() => import('../pages/manual/content/system/cs-telegram-bot.mdx')) },
  { path: '/app-disclosures/new', component: lazy(() => import('../pages/app-disclosures/FormPage')) },
  { path: '/app-disclosures/:id', component: lazy(() => import('../pages/app-disclosures/FormPage')) },
  { path: '/app-disclosures', component: lazy(() => import('../pages/app-disclosures/ListPage')) },
  // App What's New management
  {
    path: '/app-whats-new/new',
    component: lazy(() => import('../pages/app-whats-new/FormPage'))
  },
  {
    path: '/app-whats-new/:id',
    component: lazy(() => import('../pages/app-whats-new/FormPage'))
  },
  {
    path: '/app-whats-new',
    component: lazy(() => import('../pages/app-whats-new/ListPage'))
  },
  // What's New / Release Notes
  {
    path: '/whats-new',
    component: lazy(() => import('../pages/whats-new/index'))
  },
  {
    path: '/whats-new/:yearMonth/:version',
    component: lazy(() => import('../pages/whats-new/ReleasePage'))
  },
  // Super Powers
  {
    path: '/super',
    component: () => <Navigate to="/super/operators" replace />
  },
  {
    path: '/super-powers',
    component: () => <Navigate to="/super/operators" replace />
  },
  {
    path: '/super/operators',
    component: lazy(() => import('../pages/operators/index'))
  },
  // Developer Console
  {
    path: '/developer-console/digital-deposit-simulation',
    component: lazy(() => import('../pages/DeveloperConsole/DigitalDepositSimulation'))
  },
  {
    path: '/developer-console/remarks-testing',
    component: lazy(() => import('../pages/DeveloperConsole/RemarksTesting'))
  },
  {
    path: '/developer-console/card-transaction-simulation',
    component: lazy(() => import('../pages/DeveloperConsole/CardTransactionSimulation'))
  },
  {
    path: '/developer-console/card-renewal-simulation',
    component: lazy(() => import('../pages/DeveloperConsole/CardRenewalSimulation'))
  },
  {
    path: '/developer-console/generate-unissued-cards',
    component: lazy(() => import('../pages/DeveloperConsole/GenerateUnissuedCards'))
  },
  {
    path: '/developer-console/feature-flag-output-overrides',
    component: lazy(() => import('../pages/DeveloperConsole/FeatureFlagOutputOverrides'))
  },
  // Transactions
  {
    path: '/transactions/:id',
    component: lazy(() => import('../pages/transactions/detail'))
  },
  // Account Settings
  {
    path: '/account/settings',
    component: lazy(() => import('../pages/account-settings/index'))
  },
  // Cash Inventory
  {
    path: '/cash-inventory',
    component: lazy(() => import('../pages/cash-inventory/index'))
  },
  // Card Fraud Cases
  {
    path: '/card-fraud-cases/:id',
    component: lazy(() => import('../pages/card-fraud-cases/detail'))
  },
  // Fuel Sweep Management
  {
    path: '/digital-asset-treasury',
    component: lazy(() => import('../pages/digital-asset-treasury/index'))
  },
  {
    path: '/digital-asset-treasury/requests',
    component: lazy(() => import('../pages/digital-asset-treasury/requests/index'))
  },
  {
    path: '/digital-asset-treasury/requests/:requestId',
    component: lazy(() => import('../pages/digital-asset-treasury/requests/detail'))
  },
  {
    path: '/digital-asset-treasury/tasks',
    component: lazy(() => import('../pages/digital-asset-treasury/tasks/index'))
  },
  {
    path: '/digital-asset-treasury/auto-sweep',
    component: lazy(() => import('../pages/digital-asset-treasury/auto-sweep/index'))
  },
  {
    path: '/digital-asset-treasury/auto-sweep/:jobId',
    component: lazy(() => import('../pages/digital-asset-treasury/auto-sweep/detail'))
  },
  {
    path: '/digital-asset-treasury/topup-rules',
    component: lazy(() => import('../pages/digital-asset-treasury/topup-rules/index'))
  },
  {
    path: '/digital-asset-treasury/topup-rules/:ruleId',
    component: lazy(() => import('../pages/digital-asset-treasury/topup-rules/detail'))
  },
  {
    path: '/digital-asset-treasury/automation',
    component: lazy(() => import('../pages/digital-asset-treasury/automation-rules/index'))
  },
  {
    path: '/digital-asset-treasury/treasury-balances',
    component: lazy(() => import('../pages/digital-asset-treasury/treasury-balances/index'))
  },
  {
    path: '/digital-asset-treasury/tron-delegations',
    component: lazy(() => import('../pages/digital-asset-treasury/tron-delegations/index'))
  },
  {
    path: '/digital-asset-treasury/tron-delegations/:recipientAddress',
    component: lazy(() => import('../pages/digital-asset-treasury/tron-delegations/detail'))
  },

  // Audit Logs
  {
    path: '/audit-logs',
    component: lazy(() => import('../pages/audit-logs/index'))
  },

  // Audit Logs Manual
  {
    path: '/manual/audit-logs',
    component: lazy(() => import('../pages/manual/audit-logs'))
  },
  {
    path: '/manual/audit-logs/overview',
    component: lazy(() => import('../pages/manual/content/audit-logs/overview.mdx'))
  },

  // Vendor Configuration
  {
    path: '/vendor-config',
    component: lazy(() => import('../pages/vendor-config/index'))
  },
  {
    path: '/system/system-notice-emails',
    component: lazy(() => import('../pages/system-notice-email/index'))
  },
  {
    path: '/system/cs-telegram-bot',
    component: lazy(() => import('../pages/cs-telegram-bot/index'))
  },

  // Config & Secrets (System — DevOps)
  {
    path: '/system/config',
    component: lazy(() => import('../pages/config/index'))
  },

  // Client Consent Management
  {
    path: '/client-consent',
    component: lazy(() => import('../pages/client-consent/index'))
  },
  {
    path: '/client-consent/professional-investor',
    component: lazy(() => import('../pages/client-consent/professional-investor/index'))
  },
  {
    path: '/client-consent/:id',
    component: lazy(() => import('../pages/client-consent/detail'))
  },

  // Feature Readiness (System)
  {
    path: '/system/feature-readiness',
    component: lazy(() => import('../pages/system/feature-readiness/index')),
  },
  {
    path: '/system/feature-flags',
    component: lazy(() => import('../pages/system/feature-flags/index')),
  },

  // Temporal Explorer (Superadmin)
  {
    path: '/temporal/workflows',
    component: lazy(() => import('../pages/temporal/workflows/index')),
  },
  {
    path: '/temporal/workflows/:workflowId/:runId',
    component: lazy(() => import('../pages/temporal/workflows/detail')),
  },
  {
    path: '/temporal/schedules',
    component: lazy(() => import('../pages/temporal/schedules/index')),
  },
  {
    path: '/temporal/task-queues',
    component: lazy(() => import('../pages/temporal/task-queues/index')),
  },

  // Public routes (no auth required, no layout)
  {
    path: '/mfa-enrollment',
    component: lazy(() => import('../pages/mfa-enrollment/index'))
  },
  {
    path: '/account-action',
    component: lazy(() => import('../pages/account-action/index'))
  }
];
